/**
 * A clock action that hits a dead session is not lost (Oct 2026).
 *
 * The first thing people do in the morning is Check in, and that is exactly
 * when a session may have ended (7-day max age, a revoked token). The callable
 * then fails `unauthenticated`, the tap did nothing, and the person later types
 * the check-in by hand. Instead: keep the tap (with its moment) in localStorage,
 * sign out cleanly so the login page explains it, and once signed in again
 * `replayPendingClock` fires it — a check-in is then adjusted back to the
 * original moment, so the day shows the real start.
 */
import { signOut as fbSignOut } from 'firebase/auth';
import type { TimeClockInput } from '@daaily/shared';
import { auth } from '../lib/firebase';
import { SESSION_EXPIRED_KEY } from '../auth/AuthProvider';
import { callTimeClock, todayStr } from './lib';

export const PENDING_CLOCK_KEY = 'daaily.time.pendingClock';
/** A kept tap older than this is dropped (a stale device coming back days later). */
const MAX_AGE_MS = 20 * 60 * 60 * 1000;

export interface PendingClock extends TimeClockInput {
  /** When the person tapped (epoch ms). */
  atMs: number;
}

function hhmm(ms: number): string {
  return new Date(ms).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

export function readPendingClock(): PendingClock | null {
  try {
    const raw = localStorage.getItem(PENDING_CLOCK_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as PendingClock;
    if (!p?.action || !p.date || typeof p.atMs !== 'number') return null;
    if (p.date !== todayStr() || Date.now() - p.atMs > MAX_AGE_MS) { clearPendingClock(); return null; }
    return p;
  } catch { return null; }
}

export function clearPendingClock(): void {
  try { localStorage.removeItem(PENDING_CLOCK_KEY); } catch { /* per-device only */ }
}

const ACTION_LABEL: Record<TimeClockInput['action'], string> = {
  check_in: 'check-in',
  start_break: 'break start',
  end_break: 'break end',
  check_out: 'check-out',
  adjust_check_in: 'check-in adjustment',
};

/** "Your check-in at 07:31 is kept …" for the login page and the error note. */
export function pendingClockNote(p: PendingClock): string {
  return `Your ${ACTION_LABEL[p.action]} at ${hhmm(p.atMs)} is kept and will be saved as soon as you are signed in again.`;
}

function isUnauthenticated(e: unknown): boolean {
  const code = (e as { code?: string } | null)?.code ?? '';
  return code === 'functions/unauthenticated' || code === 'unauthenticated';
}

/**
 * Fire a clock action; when the session is gone, keep the tap and sign out
 * (the login page shows the note). Throws a readable error either way.
 */
export async function clockViaSession(input: TimeClockInput): Promise<void> {
  const atMs = Date.now();
  if (auth.currentUser) {
    try { await callTimeClock(input); return; } catch (e) { if (!isUnauthenticated(e)) throw e; }
  }
  const pending: PendingClock = { ...input, atMs };
  try { localStorage.setItem(PENDING_CLOCK_KEY, JSON.stringify(pending)); } catch { /* per-device only */ }
  try { sessionStorage.setItem(SESSION_EXPIRED_KEY, '1'); } catch { /* ignore */ }
  void fbSignOut(auth);
  throw new Error(`You were signed out. ${pendingClockNote(pending)}`);
}

/**
 * After sign-in: fire the kept tap. A check-in is set back to the moment of
 * the tap (adjust_check_in; silently skipped if the server refuses, e.g. the
 * day already has a break before it). Returns a label for the saved card, or
 * null when there was nothing to do.
 */
export async function replayPendingClock(): Promise<string | null> {
  const p = readPendingClock();
  if (!p) return null;
  clearPendingClock(); // never twice, even if the call fails
  const tz = new Date().getTimezoneOffset();
  await callTimeClock({ date: p.date, action: p.action, tzOffsetMinutes: tz, ...(p.checkInAtMs ? { checkInAtMs: p.checkInAtMs } : {}) });
  if (p.action === 'check_in' && p.atMs < Date.now()) {
    try { await callTimeClock({ date: p.date, action: 'adjust_check_in', checkInAtMs: p.atMs, tzOffsetMinutes: tz }); } catch { /* keep the fresh check-in */ }
  }
  return `${ACTION_LABEL[p.action][0]!.toUpperCase()}${ACTION_LABEL[p.action].slice(1)} from ${hhmm(p.atMs)} saved`;
}
